/**
 * Shared coach-profile content canvas. The flagged profile marketplace
 * row is a 1200px two-column shell (sticky booking card left, content
 * right from md). Nested About bands (photos, services, testimonials)
 * sit in the content column — they must not full-bleed to
 * the viewport, or the sticky card cannot follow the page. Tools &
 * Technology and Accreditations text lists live in the About bio, not
 * as separate lower bands. Specialized-skills chips sit immediately
 * below the rendered About bio and its read-more control, before
 * Details.
 *
 * Horizontal gutters live on the **outer** marketplace wrapper. The
 * inner canvas is a centered 1200px column with no extra horizontal
 * inset. Nested in-column bands skip the 100vw breakout and the 1200px
 * max-width so they fill the 8-column slot.
 */
import { createContext, createElement, useContext } from 'react'
import PropTypes from 'prop-types'

export const COACH_PROFILE_CONTENT_MAX_WIDTH = 1200

const CoachProfileInColumnContext = createContext(false)

/**
 * Wraps the flagged profile's content column so nested bands can
 * drop full-bleed / 1200px canvas styles.
 */
export function CoachProfileInColumn({ children }) {
  return createElement(
    CoachProfileInColumnContext.Provider,
    { value: true },
    children,
  )
}

CoachProfileInColumn.propTypes = {
  children: PropTypes.node,
}

export function useCoachProfileInColumn() {
  return useContext(CoachProfileInColumnContext)
}

export function coachProfileFullBleed() {
  return {
    width: '100vw',
    maxWidth: '100vw',
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
    boxSizing: 'border-box',
  }
}

/**
 * Full-bleed band shell with the shared horizontal gutters. Pass
 * vertical padding (and optional `md` overrides) so those merge into
 * the same breakpoint block as the gutters.
 *
 * `inColumn: true` keeps the band in the marketplace content column
 * (no 100vw breakout, no extra horizontal gutters — the 1200px shell
 * already padded the row).
 */
export function coachProfileBandOuter(theme, {
  paddingTop,
  paddingBottom,
  md: mdOverrides = {},
  fullBleed = true,
  inColumn = false,
} = {}) {
  if (inColumn) {
    return {
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '100%',
      ...(paddingTop !== undefined ? { paddingTop } : null),
      ...(paddingBottom !== undefined ? { paddingBottom } : null),
      [theme.breakpoints.up('md')]: {
        ...mdOverrides,
      },
    }
  }
  return {
    ...(fullBleed ? coachProfileFullBleed() : { boxSizing: 'border-box' }),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    ...(paddingTop !== undefined ? { paddingTop } : null),
    ...(paddingBottom !== undefined ? { paddingBottom } : null),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
      ...mdOverrides,
    },
  }
}

export function coachProfileContentCanvas({ inColumn = false } = {}) {
  if (inColumn) {
    return {
      boxSizing: 'border-box',
      width: '100%',
    }
  }
  return {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: COACH_PROFILE_CONTENT_MAX_WIDTH,
    marginLeft: 'auto',
    marginRight: 'auto',
  }
}

/**
 * Computed type for the flagged content-column `coach.title` tagline.
 * Matches the previous content-column visual `h4` clamp (~29px) but is
 * regular weight (not bold) in PGA navy (`theme.palette.secondary.main`).
 * The tagline
 * stays a `<p>` (`variant="h4"`), not a document heading. Band titles
 * use `coachProfileSectionHeading` (visual `h5`) instead. The bio sits
 * 24px under this tagline; there is no About Coach heading.
 *
 * Keep margin on the consumer.
 */
export const COACH_PROFILE_TAGLINE_FONT_SIZE = 'clamp(1.35rem, 0.9vw + 1rem, 1.875rem)'

export function coachProfileTagline(theme) {
  return {
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: 400,
    fontSize: COACH_PROFILE_TAGLINE_FONT_SIZE,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: theme.palette.secondary.main,
  }
}

/**
 * Computed type for flagged band-level section titles.
 * Foundation/Typography visual `h5` (call sites use `variant="h5"`
 * `component="h2"`), same computed size as the booking-card coach
 * name (~24px / weight 800). Services / Photos / Testimonials /
 * Request Coaching share this size. Tools & Technology / PGA
 * Accreditations stay `subtitle1` / `h3` and must not use this. Do
 * not apply this to the booking-card name (it already uses visual
 * `h5` as the document `h1`). The About bio has no section title.
 *
 * Keep margin and text-align on the consumer — those differ per band.
 */
export const COACH_PROFILE_SECTION_HEADING_FONT_SIZE = '24px'

export function coachProfileSectionHeading(theme) {
  return {
    fontFamily: theme.typography.h5.fontFamily,
    fontWeight: 800,
    fontSize: COACH_PROFILE_SECTION_HEADING_FONT_SIZE,
    lineHeight: 1.2,
    letterSpacing: 0,
    color: theme.palette.secondary.dark,
  }
}

/**
 * Space above flagged band-level section titles (Services, Photos,
 * Articles, Testimonials, Request Coaching). Each band owns
 * `theme.spacing(6)` (48px) as `paddingTop` or `marginTop`. Do not
 * also keep a matching `paddingBottom` on the previous band or the
 * gap doubles. When articles are absent, Testimonials still own the
 * 48px above themselves. The About bio sits 24px (`theme.spacing(3)`)
 * under the tagline, or under the membership banner when that
 * banner renders, and must not use this helper. Tools & Technology
 * / PGA Accreditations stay tighter under the membership banner.
 * Coaching Services (`CoachServicesPanel`) is unmounted on flagged
 * profiles.
 */
export const COACH_PROFILE_BAND_SECTION_GAP = 6

export function coachProfileBandSectionGap(theme, { property = 'paddingTop' } = {}) {
  return {
    [property]: theme.spacing(COACH_PROFILE_BAND_SECTION_GAP),
  }
}

/** Sticky booking card + flagged multi-facility accordion shell. */
export function coachProfileBookingCardChrome(theme) {
  return {
    backgroundColor: theme.palette.common.white,
    border: '1px solid #e8ecf2',
    borderRadius: 20,
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
  }
}

/** Shared soft profile callout surface for flagged profile tips. */
export function coachProfileTipSurface(theme) {
  return {
    backgroundColor: theme.palette.blue.light25,
    border: '1px solid #dfe3ea',
    borderRadius: 20,
    boxShadow: 'none',
  }
}

export function coachProfilePillCtaBase() {
  return {
    width: '100%',
    height: 48,
    minHeight: 48,
    padding: '0 24px',
    margin: 0,
    borderRadius: 999,
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.9375rem',
    boxSizing: 'border-box',
    boxShadow: 'none',
  }
}

/** Filled View Services / Get directions pill on the flagged profile. */
export function coachProfilePillCtaPrimary(theme) {
  return {
    ...coachProfilePillCtaBase(),
    '&&': {
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.blue.main}`,
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.common.white,
      },
    },
  }
}

/** Outline Send a Request / Open in Google Maps pill on the flagged profile. */
export function coachProfilePillCtaSecondary(theme) {
  return {
    ...coachProfilePillCtaBase(),
    '&&': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.blue.main,
      border: '1px solid #dfe3ea',
      '&:hover': {
        backgroundColor: '#ECF2F9',
        color: theme.palette.blue.main,
        borderColor: theme.palette.blue.main,
      },
    },
  }
}

/** Offering-card / facility meta row (Lucide gold glyph + label). */
export function coachProfileMetaRow(theme) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.75),
    minWidth: 0,
  }
}

/**
 * Horizontal offering card + facilities accordion: inline 16px Lucide + label
 * (`rowInlineMeta` in coach-profile-offering-card).
 */
export function coachProfileRowInlineMeta(theme) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    fontSize: theme.typography.subtitle2.fontSize,
    lineHeight: theme.typography.subtitle2.lineHeight,
    fontWeight: theme.typography.subtitle2.fontWeight,
    letterSpacing: theme.typography.subtitle2.letterSpacing,
    '& svg': {
      width: 16,
      height: 16,
      flexShrink: 0,
    },
  }
}

/** Blue location line on offering cards and facility address/website rows. */
export function coachProfileRowLocationMeta(theme) {
  return {
    color: theme.palette.blue.main,
  }
}

/** Ellipsis-friendly meta label (`rowLocation` / `rowMetaText`). */
export function coachProfileRowMetaEllipsisText(theme) {
  return {
    color: 'inherit',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
    },
  }
}

/**
 * Native link inside a blue facility meta row (maps / website). Matches offering
 * location typography; underline on hover/focus only.
 */
export function coachProfileFacilityMetaLink(theme) {
  return {
    ...coachProfileRowMetaEllipsisText(theme),
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    flex: 1,
    color: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    fontWeight: theme.typography.subtitle2.fontWeight,
    letterSpacing: 'inherit',
    fontFamily: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      color: 'inherit',
      textDecoration: 'underline',
      textUnderlineOffset: 3,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  }
}

export function coachProfileMetaIconColor(theme) {
  return theme.palette.gold.main
}

export function coachProfileMetaText(theme) {
  return {
    fontWeight: 600,
    color: theme.palette.grey.dark,
    letterSpacing: 0,
    lineHeight: 1.3,
    flex: 1,
    minWidth: 0,
    fontSize: theme.typography.pxToRem(14),
  }
}

export function coachProfileInlineTextLink(theme) {
  return {
    color: theme.palette.blue.main,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    '&:hover, &:focus-visible': {
      color: theme.palette.secondary.light,
      textDecoration: 'underline',
    },
  }
}

/**
 * Booking-card maps link text (facility, city, state). Underline lives on
 * `coachProfileSimpleLocationCopy` so hover can recolor the whole control.
 */
export function coachProfileSimpleLocationLink(theme, {
  textAlign = 'center',
  inMetaRow = false,
  linkColor = 'secondary',
} = {}) {
  const color = linkColor === 'primary'
    ? theme.palette.text.primary
    : theme.palette.text.secondary

  return {
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    ...(inMetaRow ? { flex: 1, minWidth: 0 } : {}),
    color,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: 'inherit',
    textAlign,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      color: theme.palette.blue.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  }
}

export function coachProfileSimpleLocationCopy() {
  return {
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  }
}
